import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import ResultsPage from "./pages/ResultsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ReportPage from "./pages/ReportPage";
import DashboardPage from "./pages/DashboardPage";
import ComparisonPage from "./pages/ComparisonPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import ForumPage from "./pages/ForumPage";
import SampleReportPage from "./pages/SampleReportPage";
import SharedReportPage from "./pages/SharedReportPage";
import UpgradePage from "./pages/UpgradePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "expatmatch_token";

// Token storage helpers.
// We use localStorage for the JWT (Bearer auth pattern). Tokens are JWT-formatted
// (header.payload.signature) — anything else is treated as tampered and discarded.
// This validation runs synchronously on load and on every set() call.
const isValidJwtShape = (value) =>
  typeof value === "string" &&
  value.split(".").length === 3 &&
  value.length < 4096;

const readToken = () => {
  try {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored && !isValidJwtShape(stored)) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

const writeToken = (value) => {
  try {
    if (value && isValidJwtShape(value)) {
      localStorage.setItem(TOKEN_KEY, value);
    }
  } catch (e) {
    console.warn("Token storage failed:", e?.message);
  }
};

const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch {
          clearToken();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    writeToken(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, referralCode) => {
    const payload = { name, email, password };
    if (referralCode) payload.referral_code = referralCode;
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData, referral_applied } = response.data;
    writeToken(newToken);
    setToken(newToken);
    setUser(userData);
    return { user: userData, referralApplied: Boolean(referral_applied) };
  };

  const logout = () => {
    clearToken();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-background">
        <div className="noise-overlay" />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route 
              path="/questionnaire" 
              element={
                <ProtectedRoute>
                  <QuestionnairePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/results" 
              element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment-success" 
              element={
                <ProtectedRoute>
                  <PaymentSuccessPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/report/:countryName" 
              element={
                <ProtectedRoute>
                  <ReportPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/compare" 
              element={
                <ProtectedRoute>
                  <ComparisonPage />
                </ProtectedRoute>
              } 
            />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/forum" element={<ForumPage />} />
            <Route path="/sample-report" element={<SampleReportPage />} />
            <Route path="/sample-report/:countryName" element={<SampleReportPage />} />
            <Route path="/shared/:shareToken" element={<SharedReportPage />} />
            <Route path="/upgrade" element={<UpgradePage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
